import {
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from 'react-query'

import { AxiosError } from 'axios'

import { apiClients, makeClientQueryFn } from './lib'

import { useNotifications } from '../reduxHooks'

import {
  AssignAccountsRequest,
  CreateOAuthInvitationRequest,
  CreateOAuthInvitationResponse,
  AccountListing,
  InvalidAssignmentBatchDetail,
  Nullable,
  OAuthInvitation,
  OAuthInvitationCallbackRequest,
  PlatformUnitAccessCredential,
  PlatformUnitParams,
  PublicOAuthInvitation,
} from '@percept/types'


export const usePublicOAuthInvitation = (
  token: string | null,
  options?: UseQueryOptions<PublicOAuthInvitation, AxiosError>,
): UseQueryResult<PublicOAuthInvitation, AxiosError> => {
  return useQuery<PublicOAuthInvitation, AxiosError>({
    queryKey: ['publicOAuthInvitation', token],
    queryFn: makeClientQueryFn<PublicOAuthInvitation>(
      apiClients.corePublic,
      { url: `/oauth-invitation/${token}` },
    ),
    enabled: !!token,
    retry: false,
    cacheTime: 0,
    staleTime: 0,
    ...options,
  })
}


type CompleteOAuthInvitationMutationProps = (
  OAuthInvitationCallbackRequest & {
    token: string
  }
)

export const useCompleteOAuthInvitation = (): UseMutationResult<
  PublicOAuthInvitation, AxiosError, CompleteOAuthInvitationMutationProps, unknown
> => {
  return useMutation<
    PublicOAuthInvitation, AxiosError, CompleteOAuthInvitationMutationProps, unknown
  >({
    mutationKey: ['completeOAuthInvitation'],
    mutationFn: async ({ token, code, state }) => {
      const response = await apiClients.corePublic.post<PublicOAuthInvitation>(
        `/oauth-invitation/${token}`,
        { code, state },
      )
      return response.data
    },
  })
}


export const useOAuthInvitations = ({
  org_unit_id
}: Nullable<PlatformUnitParams>): UseQueryResult<OAuthInvitation[], AxiosError> => {
  return useQuery<OAuthInvitation[], AxiosError>({
    queryKey: ['oauthInvitations', org_unit_id],
    queryFn: makeClientQueryFn<OAuthInvitation[]>(
      apiClients.core,
      { url: `/org-units/${org_unit_id}/oauth-invitations` },
    ),
    enabled: !!org_unit_id,
  })
}


export const useCreateOAuthInvitation = ({
  org_unit_id,
  onSuccess,
}: Nullable<PlatformUnitParams> & {
  onSuccess?: (data: CreateOAuthInvitationResponse) => void
}): UseMutationResult<
  CreateOAuthInvitationResponse, AxiosError, CreateOAuthInvitationRequest, unknown
> => {
  const queryClient = useQueryClient()
  const [, addNotification] = useNotifications()
  return useMutation<
    CreateOAuthInvitationResponse, AxiosError, CreateOAuthInvitationRequest, unknown
  >({
    mutationKey: ['createOAuthInvitation', org_unit_id],
    mutationFn: async (payload) => {
      const response = await apiClients.core.post<CreateOAuthInvitationResponse>(
        `/org-units/${org_unit_id}/oauth-invitations`,
        payload,
      )
      return response.data
    },
    onMutate: () => {
      addNotification({
        id: 'create-oauth-invitation',
        type: 'info',
        loading: true,
        message: 'Creating OAuth invitation...',
        ttl: 0,
      })
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(['oauthInvitations'])
      addNotification({
        id: 'create-oauth-invitation',
        type: 'success',
        loading: false,
        message: (
          data.email_sent_at
            ? 'Invitation created and email dispatched'
            : 'Invitation created (email dispatch failed — share the link directly)'
        ),
      })
      onSuccess && onSuccess(data)
    },
    onError: () => {
      addNotification({
        id: 'create-oauth-invitation',
        type: 'error',
        loading: false,
        message: 'OAuth invitation could not be created',
      })
    },
  })
}


type InvitationIdMutationProps = {
  id: string
}

export const useRevokeOAuthInvitation = ({
  onSuccess,
}: {
  onSuccess?: () => void
} = {}): UseMutationResult<
  OAuthInvitation, AxiosError, InvitationIdMutationProps, unknown
> => {
  const queryClient = useQueryClient()
  const [, addNotification] = useNotifications()
  return useMutation<
    OAuthInvitation, AxiosError, InvitationIdMutationProps, unknown
  >({
    mutationKey: ['revokeOAuthInvitation'],
    mutationFn: async ({ id }) => {
      const response = await apiClients.core.post<OAuthInvitation>(
        `/oauth-invitations/${id}/revoke`,
      )
      return response.data
    },
    onMutate: () => {
      addNotification({
        id: 'revoke-oauth-invitation',
        type: 'info',
        loading: true,
        message: 'Revoking invitation...',
        ttl: 0,
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['oauthInvitations'])
      addNotification({
        id: 'revoke-oauth-invitation',
        type: 'success',
        loading: false,
        message: 'Invitation revoked',
      })
      onSuccess && onSuccess()
    },
    onError: () => {
      addNotification({
        id: 'revoke-oauth-invitation',
        type: 'error',
        loading: false,
        message: 'Invitation could not be revoked',
      })
    },
  })
}


export const useReissueOAuthInvitation = ({
  onSuccess,
}: {
  onSuccess?: (data: CreateOAuthInvitationResponse) => void
} = {}): UseMutationResult<
  CreateOAuthInvitationResponse, AxiosError, InvitationIdMutationProps, unknown
> => {
  const queryClient = useQueryClient()
  const [, addNotification] = useNotifications()
  return useMutation<
    CreateOAuthInvitationResponse, AxiosError, InvitationIdMutationProps, unknown
  >({
    mutationKey: ['reissueOAuthInvitation'],
    mutationFn: async ({ id }) => {
      const response = await apiClients.core.post<CreateOAuthInvitationResponse>(
        `/oauth-invitations/${id}/reissue`,
      )
      return response.data
    },
    onMutate: () => {
      addNotification({
        id: 'reissue-oauth-invitation',
        type: 'info',
        loading: true,
        message: 'Reissuing invitation...',
        ttl: 0,
      })
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(['oauthInvitations'])
      addNotification({
        id: 'reissue-oauth-invitation',
        type: 'success',
        loading: false,
        message: (
          data.email_sent_at
            ? 'Invitation reissued and email dispatched'
            : 'Invitation reissued (email dispatch failed — share the link directly)'
        ),
      })
      onSuccess && onSuccess(data)
    },
    onError: () => {
      addNotification({
        id: 'reissue-oauth-invitation',
        type: 'error',
        loading: false,
        message: 'Invitation could not be reissued',
      })
    },
  })
}


type DiscoverableAccountsParams = (
  PlatformUnitParams & {
    org_unit_provider_account_id: string
  }
)

export const useDiscoverableAccounts = ({
  org_unit_id,
  org_unit_provider_account_id,
  enabled = true,
}: Nullable<DiscoverableAccountsParams> & {
  enabled?: boolean
}): UseQueryResult<AccountListing[], AxiosError> => {
  return useQuery<AccountListing[], AxiosError>({
    queryKey: ['discoverableAccounts', org_unit_id, org_unit_provider_account_id],
    queryFn: makeClientQueryFn<AccountListing[]>(
      apiClients.core,
      {
        url: (
          `/org-units/${org_unit_id}/access-credentials/${org_unit_provider_account_id}/discoverable-accounts`
        ),
      },
    ),
    enabled: !!(org_unit_id && org_unit_provider_account_id && enabled),
    retry: false,
    cacheTime: 1000 * 60,
    staleTime: 1000 * 60,
  })
}


export type AssignAccountsError = AxiosError<{ detail: InvalidAssignmentBatchDetail }>

type AssignAccountsMutationProps = (
  DiscoverableAccountsParams & AssignAccountsRequest
)

export const useAssignAccounts = ({
  onSuccess,
}: {
  onSuccess?: (data: PlatformUnitAccessCredential[]) => void
} = {}): UseMutationResult<
  PlatformUnitAccessCredential[], AssignAccountsError, AssignAccountsMutationProps, unknown
> => {
  const queryClient = useQueryClient()
  const [, addNotification] = useNotifications()
  return useMutation<
    PlatformUnitAccessCredential[], AssignAccountsError, AssignAccountsMutationProps, unknown
  >({
    mutationKey: ['assignAccounts'],
    mutationFn: async ({ org_unit_id, org_unit_provider_account_id, assignments }) => {
      const response = await apiClients.core.post<PlatformUnitAccessCredential[]>(
        (
          `/org-units/${org_unit_id}/access-credentials/${org_unit_provider_account_id}/assign-accounts`
        ),
        { assignments },
      )
      return response.data
    },
    onMutate: ({ assignments }) => {
      addNotification({
        id: 'assign-accounts',
        type: 'info',
        loading: true,
        message: `Assigning ${assignments.length} account${assignments.length === 1 ? '' : 's'}...`,
        ttl: 0,
      })
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(['discoverableAccounts'])
      queryClient.invalidateQueries(['orgUnitProviderAccounts'])
      queryClient.invalidateQueries(['platformUnits'])
      queryClient.invalidateQueries(['orgUnits'])
      addNotification({
        id: 'assign-accounts',
        type: 'success',
        loading: false,
        message: `${data.length} account${data.length === 1 ? '' : 's'} assigned`,
      })
      onSuccess && onSuccess(data)
    },
    onError: (error) => {
      const status = error.response && error.response.status
      const message = (
        status === 413 ? 'Batch too large — assign 500 accounts at a time' :
          status === 422 ? 'Some assignments could not be made — see highlighted rows' :
            'Accounts could not be assigned'
      )
      addNotification({
        id: 'assign-accounts',
        type: 'error',
        loading: false,
        message,
      })
    },
  })
}
